import { useState, useEffect, useMemo } from "react"
import Select from 'react-select'
import { FiClock } from "react-icons/fi"
import { TbClipboardList } from "react-icons/tb"
import api from '../../../../utils/api'
import SelectStyles from "../../../../shared/Select/SelectStyle"
import Input from "../../../../shared/components/form/input"
import Textarea from "../../../../shared/components/form/textarea"
import Button from "../../../../shared/components/Button/Button"
import FormLayout from "../../../../shared/components/FormLayout/FormLayout"
import RequiredMark from "../../../../shared/components/form/RequiredMark"
import FormSection from "../../../../shared/components/FormLayout/FormSection"
import Modal from "../../../../shared/modal/Modal"
import TechnicianAgendaModal from "./TechnicianAgendaModal"
import { formatCPF, formatCNPJ } from "../../../../utils/formatters"
import './FormServiceOrder.css'
import useGroupOrders from '../../../../hooks/useGroupOrders'
import { formatLocation, resolveServiceAddress } from '../../../../utils/generatorLocation'
import { buildChecklistSections } from '../../../../utils/checklistSections'

const STATUS_OPTIONS = [
    { label: 'Aberta', value: 'aberta', selectName: 'status' },
    { label: 'Em andamento', value: 'em_andamento', selectName: 'status' },
    { label: 'Em análise', value: 'em_analise', selectName: 'status' },
    { label: 'Concluída', value: 'concluida', selectName: 'status' },
]

function getTodayDate() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const OVERDUE_FIELDS = [
    { field: 'nextInspectionRadiatorDate', label: 'Inspeção do Radiador' },
    { field: 'nextInspectionInjectionPumpDate', label: 'Bomba de Injeção' },
    { field: 'nextChangeBatteryDate', label: 'Troca de Bateria' },
    { field: 'nextAlternatorMegageDate', label: 'Megagem do Alternador' },
    { field: 'nextEquipmentSanitizationDate', label: 'Sanitização' },
    { field: 'nextTankCleaningDate', label: 'Limpeza do Tanque' },
    { field: 'nextHorimetroChangeOilAndFilterDate', label: 'Troca de Óleo e Filtro' },
]

function buildOverdueInfo(generator) {
    const now = Date.now()
    const overdue = OVERDUE_FIELDS
        .filter(({ field }) => generator[field] && new Date(generator[field]).getTime() <= now)
        .map(({ field, label }) => `- ${label} (vencida desde ${new Date(generator[field]).toLocaleDateString('pt-BR')})`)

    if (overdue.length === 0) return ''
    return `Manutenções vencidas:\n${overdue.join('\n')}`
}

function FormServiceOrder({ handleSubmit, serviceOrderData, btnText, btCancelAction, title }) {

    const isEditing = Boolean(serviceOrderData?._id)
    const [serviceOrder, setServiceOrder] = useState(serviceOrderData || { status: STATUS_OPTIONS[0], openDate: getTodayDate() })
    const [clients, setClients] = useState([])
    const [client, setClient] = useState(serviceOrderData?.client || null)
    const [generatorOptions, setGeneratorOptions] = useState([])
    const [technicianOptions, setTechnicianOptions] = useState([])
    const [procedureOptions, setProcedureOptions] = useState([])
    const [allServiceOrders, setAllServiceOrders] = useState([])
    const [modalAgenda, setModalAgenda] = useState(false)
    const [checklistOpen, setChecklistOpen] = useState(false)
    const [token] = useState(localStorage.getItem('token') || '')
    const [lastAutoTechnicianInfo, setLastAutoTechnicianInfo] = useState('')

    useEffect(() => {
        Promise.all([
            api.get('/client/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/user/technicians', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/serviceOrder/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/procedure/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
        ]).then(([clientRes, techRes, serviceOrderRes, procedureRes]) => {
            setClients(clientRes.data.clients)
            setTechnicianOptions(techRes.data.technicians.map(t => ({
                ...t,
                label: t.name,
                value: t._id,
            })))
            setAllServiceOrders(serviceOrderRes.data.serviceOrders || [])
            setProcedureOptions((procedureRes.data.procedures || [])
                .filter(p => !p.disabled)
                .map(p => ({
                    ...p,
                    label: p.name,
                    value: p._id,
                    selectName: 'procedure',
                })))
        })
    }, [token])

    useEffect(() => {
        if (client?.generators?.length) {
            setGeneratorOptions(client.generators.map(g => ({
                ...g,
                label: g.name,
                value: g._id,
                selectName: 'generator',
            })))
        } else {
            setGeneratorOptions([])
        }
    }, [client])

    function handleChange(e) {
        setServiceOrder(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    async function handleSelectChange(selectedOption) {
        if (selectedOption.selectName === 'client') {
            setClient(selectedOption)
            setServiceOrder(prev => ({ ...prev, client: selectedOption, generator: null }))
        } else if (selectedOption.selectName === 'procedure') {
            setServiceOrder(prev => ({
                ...prev,
                procedure: selectedOption,
                // congela a ordem atual do grupo na OS (mudanças futuras só valem pras próximas OS)
                checklist: (selectedOption.items || []).map(item => ({
                    ...item,
                    group: item.group ? { ...item.group, order: groupOrders[item.group.value] } : item.group,
                })),
            }))
        } else if (selectedOption.selectName === 'generator') {
            setServiceOrder(prev => ({ ...prev, generator: selectedOption }))

            const freshGenerator = await api.get(`/generator/get/${selectedOption._id}`, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then(response => response.data.generator).catch(() => null)

            if (!freshGenerator) return

            const autoInfo = buildOverdueInfo(freshGenerator)
            setServiceOrder(prev => {
                const currentInfo = prev.technicianInfo || ''
                if (currentInfo && currentInfo !== lastAutoTechnicianInfo) return prev
                return { ...prev, technicianInfo: autoInfo }
            })
            setLastAutoTechnicianInfo(autoInfo)
        } else {
            setServiceOrder(prev => ({ ...prev, [selectedOption.selectName]: selectedOption }))
        }
    }

    function handleTechniciansChange(selected) {
        setServiceOrder(prev => ({
            ...prev,
            technicians: selected || [],
            scheduledDate: undefined,
            scheduledStartTime: undefined,
            scheduledEndTime: undefined,
        }))
    }

    function handleConfirmSchedule({ scheduledDate, scheduledStartTime, scheduledEndTime }) {
        setServiceOrder(prev => ({ ...prev, scheduledDate, scheduledStartTime, scheduledEndTime }))
        setModalAgenda(false)
    }

    function submit(e) {
        e.preventDefault()
        handleSubmit(serviceOrder)
    }

    // Local do serviço: o salvo na OS (congelado) ou, numa OS nova, o calculado do gerador escolhido.
    const serviceAddress = serviceOrder.serviceAddress
        || (serviceOrder.generator ? resolveServiceAddress(client || serviceOrder.client, serviceOrder.generator) : null)

    const groupOrders = useGroupOrders()

    const checklistSections = useMemo(
        () => buildChecklistSections(serviceOrder.checklist || []),
        [serviceOrder.checklist]
    )

    const genPlaceholder = !client?.value
        ? 'Selecione um cliente primeiro'
        : generatorOptions.length === 0
        ? 'Nenhum gerador vinculado a este cliente'
        : 'Selecione o gerador'

    return (
        <FormLayout requiredLegend
            title={title}
            onSubmit={submit}
            actions={
                <>
                    <Button variant="save" type="submit">
                        {btnText || 'Salvar'}
                    </Button>
                    {btCancelAction && (
                        <Button variant="cancel" onClick={btCancelAction}>
                            Cancelar
                        </Button>
                    )}
                </>
            }
        >
            {/* ── Informações da OS ── */}
            <FormSection label="Informações da OS">
                <div className="so-row">
                    <div className="so-col-narrow form-group">
                        <Input
                            text="Número da OS"
                            type="text"
                            name="code"
                            placeholder="Gerado automaticamente"
                            handleOnChange={() => {}}
                            value={serviceOrder.code || ''}
                            disabled={true}
                        />
                    </div>
                    <div className="so-col-mid form-group">
                        <label className="input-label">Status:</label>
                        <Select
                            options={STATUS_OPTIONS}
                            onChange={handleSelectChange}
                            styles={SelectStyles}
                            menuPortalTarget={document.body}
                            value={serviceOrder.status || null}
                            placeholder="Selecione o status"
                            isDisabled={!isEditing}
                        />
                    </div>
                    <div className="so-col-narrow form-group">
                        <Input
                            text="Data de Abertura"
                            type="date"
                            name="openDate"
                            handleOnChange={handleChange}
                            value={serviceOrder.openDate || ''}
                        />
                    </div>
                </div>
            </FormSection>

            {/* ── Cliente ── */}
            <FormSection label={<>Cliente<RequiredMark /></>}>
                <div className="form-group">
                    <Select
                        options={clients}
                        onChange={handleSelectChange}
                        styles={SelectStyles}
                        menuPortalTarget={document.body}
                        value={serviceOrder.client || null}
                        placeholder="Selecione o cliente"
                    />
                </div>
                {client?.value && (
                    <div className="so-client-card">
                        <span className="so-client-card-label">Dados do cliente</span>
                        {client.personType === 'juridica' && (
                            <>
                                <div className="so-col-wide form-group">
                                    <Input text="Razão Social" type="text" name="corporateName" handleOnChange={() => {}} value={client.corporateName || ''} disabled={true} />
                                </div>
                                <div className="so-col-wide form-group">
                                    <Input text="Nome Fantasia" type="text" name="fantasyName" handleOnChange={() => {}} value={client.fantasyName || ''} disabled={true} />
                                </div>
                                <div className="so-col-narrow form-group">
                                    <Input text="CNPJ" type="text" name="CNPJ" handleOnChange={() => {}} value={formatCNPJ(client.CNPJ)} disabled={true} />
                                </div>
                            </>
                        )}
                        {client.personType === 'fisica' && (
                            <>
                                <div className="so-col-wide form-group">
                                    <Input text="Nome Completo" type="text" name="completeName" handleOnChange={() => {}} value={client.completeName || ''} disabled={true} />
                                </div>
                                <div className="so-col-narrow form-group">
                                    <Input text="CPF" type="text" name="CPF" handleOnChange={() => {}} value={formatCPF(client.CPF)} disabled={true} />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </FormSection>

            {/* ── Gerador ── */}
            <FormSection label={<>Gerador<RequiredMark /></>}>
                <div className="form-group">
                    <Select
                        options={generatorOptions}
                        onChange={handleSelectChange}
                        styles={SelectStyles}
                        menuPortalTarget={document.body}
                        value={serviceOrder.generator || null}
                        placeholder={genPlaceholder}
                        isDisabled={!client?.value || generatorOptions.length === 0}
                    />
                </div>
                {serviceAddress && serviceAddress.logradouro && (
                    <div className="so-client-card">
                        <span className="so-client-card-label">Local do serviço</span>
                        <div className="so-col-wide form-group">
                            <Input text="Endereço" type="text" name="serviceAddress" handleOnChange={() => {}} value={formatLocation(serviceAddress)} disabled={true} />
                        </div>
                        <div className="so-col-wide form-group">
                            <span className="so-client-card-label">{serviceAddress.sameAsClient ? 'Mesmo endereço do cliente' : 'Endereço próprio do gerador'}</span>
                        </div>
                    </div>
                )}
            </FormSection>

            {/* ── Equipe e Agendamento ── */}
            <FormSection label="Equipe e Agendamento">
                <div className="form-group">
                    <label className="input-label">Técnico(s):</label>
                    <Select
                        isMulti
                        options={technicianOptions}
                        onChange={handleTechniciansChange}
                        styles={SelectStyles}
                        menuPortalTarget={document.body}
                        value={serviceOrder.technicians || []}
                        placeholder="Selecione o(s) técnico(s)"
                    />
                </div>
                <div className="so-schedule-row form-group">
                    <button
                        type="button"
                        className="so-btn-vivid"
                        onClick={() => setModalAgenda(true)}
                        disabled={!serviceOrder.technicians || serviceOrder.technicians.length === 0}
                    >
                        <FiClock size={16} />
                        Escolher horário
                    </button>
                    <span className="so-schedule-info">
                        {serviceOrder.scheduledDate
                            ? `Agendado: ${serviceOrder.scheduledDate.split('-').reverse().join('/')} das ${serviceOrder.scheduledStartTime} às ${serviceOrder.scheduledEndTime}`
                            : 'Nenhum horário selecionado'}
                    </span>
                </div>
                <div className="form-group">
                    <Textarea
                        text="Informações ao Técnico"
                        name="technicianInfo"
                        placeholder="Instruções ou informações úteis para o(s) técnico(s)"
                        handleOnChange={handleChange}
                        value={serviceOrder.technicianInfo || ''}
                        rows={8}
                    />
                </div>
            </FormSection>

            {/* ── Procedimento ── */}
            <FormSection label={<>Procedimento<RequiredMark /></>}>
                <div className="form-group">
                    <Select
                        options={procedureOptions}
                        onChange={handleSelectChange}
                        styles={SelectStyles}
                        menuPortalTarget={document.body}
                        value={serviceOrder.procedure || null}
                        placeholder="Selecione o procedimento"
                    />
                </div>

                {serviceOrder.procedure && (
                    <div className="so-schedule-row form-group">
                        <button type="button" className="so-btn-vivid" onClick={() => setChecklistOpen(true)}>
                            <TbClipboardList size={16} />
                            Ver checklist
                        </button>
                        <span className="so-schedule-info">
                            {(serviceOrder.checklist || []).length} {(serviceOrder.checklist || []).length === 1 ? 'item' : 'itens'} no checklist desta OS
                        </span>
                    </div>
                )}
            </FormSection>

            <div className="so-bottom-spacer" />

            {modalAgenda && (
                <TechnicianAgendaModal
                    technicians={serviceOrder.technicians || []}
                    serviceOrders={allServiceOrders}
                    excludeOrderId={serviceOrderData?._id}
                    initialDate={serviceOrder.scheduledDate}
                    onConfirm={handleConfirmSchedule}
                    onClose={() => setModalAgenda(false)}
                />
            )}

            <Modal
                modalStatus={checklistOpen}
                changeStatus={() => setChecklistOpen(false)}
                w={70}
                content={
                    <div className="so-checklist-modal">
                        <div className="so-checklist-modal-header">
                            <p className="so-checklist-modal-title">Checklist do procedimento</p>
                            <span className="so-checklist-modal-title-accent" />
                            <span className="so-checklist-modal-subtitle">{serviceOrder.procedure?.name || serviceOrder.procedure?.label}</span>
                        </div>
                        <div className="so-checklist-list">
                            {checklistSections.length > 0 ? (
                                checklistSections.map((section) => (
                                    <div key={section.groupLabel} className="so-checklist-group">
                                        <div className="so-checklist-group-header">{section.groupLabel}</div>
                                        {section.items.map((item) => (
                                            <div key={item._id} className="so-checklist-item">
                                                <span className="so-checklist-item-name">{item.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                <p className="so-checklist-empty">Nenhum item no checklist desta OS</p>
                            )}
                        </div>
                    </div>
                }
            />

        </FormLayout>
    )
}

export default FormServiceOrder
